package com.milta.feature.route.presentation

import com.milta.core.ui.component.ProcessingProgress
import com.milta.feature.importjobs.presentation.ImportJobFailureKind
import com.milta.feature.imports.domain.entity.ExtractedDelivery
import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.DeliveryDraft
import com.milta.feature.route.domain.entity.GeocodeFailureReason
import com.milta.feature.route.domain.entity.GeocodeStatus
import com.milta.feature.route.domain.entity.addressFixReason
import com.milta.feature.route.domain.entity.LoadingAddress
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.spreadsheetimport.domain.entity.ImportedDeliveryRow
import com.milta.feature.team.domain.entity.Driver
import kotlinx.serialization.Serializable

/** Mirrors the backend `RouteBatchLimits.MaxDeliveriesPerBatch` (`RouteBatches/RouteBatchLimits.cs`)
 * — checked right after import (both spreadsheet and PDF paths) so an oversized file is rejected
 * before the manager spends time reviewing/correcting rows on Écran 2, not only at "Envoyer". */
const val MAX_STOPS = 500

/**
 * Which of the mockup's screens (`docs/mockups/MIL-50.html`, Écrans 1/1b/1c/1d/2, plus Écran 3
 * added by MIL-51) is currently shown on the web dashboard's "Ajouter une tournée" flow. Mirrors
 * the mobile `SpreadsheetImportPhase` (same `POST /spreadsheet-imports` contract, MIL-27) but adds
 * [VerifyAssign]/[BatchResult] since this flow — unlike mobile — combines import, driver
 * assignment and batch dispatch confirmation on a single wizard instead of separate
 * screens/navigation.
 */
sealed interface WebImportRoutePhase {
    /** Écran 1 — dropzone, no file picked yet. */
    data object Idle : WebImportRoutePhase

    /** Maquette async-imports-v2, web écran 1 — the file reached the server and the import runs
     * server-side (`/import-jobs`): the manager may leave the page, it is found again on return. */
    data object Analysing : WebImportRoutePhase

    /** Écran 1b — 400 `error.validation.invalid_file` / `error.import.unsupported_format`. */
    data object InvalidFile : WebImportRoutePhase

    /** Écran 1c — 422 on upload, or an import that failed as unreadable. */
    data object ExtractionFailed : WebImportRoutePhase

    /**
     * Écran 1d (maquette async-imports-v2, web écran 2) — the analysis service failed. [offersRetry]:
     * "Réessayer" relaunches the stored file server-side (or re-sends the local one if it never
     * reached the server); false once the attempts are used — "Choisir un autre fichier" instead.
     */
    data class ServiceUnavailable(val offersRetry: Boolean = true) : WebImportRoutePhase

    /**
     * The import failed on the document itself (too long, too large, no deliveries, file expired)
     * or was cancelled because its origin route was deleted — the mobile import screens' texts,
     * only "Choisir un autre fichier" is offered.
     */
    data class JobFailed(val kind: ImportJobFailureKind) : WebImportRoutePhase

    /**
     * MIL-98 — E1, AI credit at 0. A PDF ([isSpreadsheet] false) is refused before any upload (or by
     * the server's 403 / the job stopped at start); a spreadsheet only when its columns needed the
     * AI (R7). Only "Choisir un autre fichier" is offered — no manual entry on the web.
     */
    data class AiCreditExhausted(
        val isSpreadsheet: Boolean,
        /** Reset date carried by the 403; null otherwise (the screen then uses the last known credit). */
        val resetAt: String? = null,
    ) : WebImportRoutePhase

    /**
     * The account's active import (one at a time) was launched from the mobile app for an existing
     * route, or is a photo scan: its deliveries belong to that route, so it is never validated
     * here — shown until it is finished on the phone.
     */
    data object ImportOnMobile : WebImportRoutePhase

    /** No HTTP response at all — mirrors the mobile flow's own addition for the same gap (no
     * dedicated mockup screen), reuses [com.milta.core.localization.ImportsStrings] offline wording. */
    data object NetworkUnavailable : WebImportRoutePhase

    /** Écran 2 (+ 2b/2c/2d variants) — file imported, driver assignment + stops table. */
    data object VerifyAssign : WebImportRoutePhase

    /** Import succeeded (spreadsheet or PDF) but yielded more than [MAX_STOPS] rows — rejected
     * immediately rather than letting the manager reach Écran 2, mirrors the backend's own
     * `RouteBatchLimits.MaxDeliveriesPerBatch` check on `POST /routes`. [stopCount] is the number
     * of rows actually detected, shown in the error message. */
    data class TooManyStops(val stopCount: Int) : WebImportRoutePhase

    /** Retour 26/10 (demande 2, R6) — "1 Importer" clicked while a list exists: the drop zone again;
     * a file dropped there is ADDED to the list (R9), the list itself stays as it is. */
    data object AddFile : WebImportRoutePhase

    /** Écran 3 (MIL-51) — `POST /route-batches` succeeded, shows the per-driver split
     * ([WebImportRouteState.batchResult]) before the manager confirms with "Valider et envoyer les
     * tournées" (`POST /route-batches/{id}/dispatch`). No dedicated data here (mirrors [VerifyAssign]
     * itself): the actual payload lives in [WebImportRouteState.batchResult]/`batchStopsByRouteId`. */
    data object BatchResult : WebImportRoutePhase
}

/**
 * One row of the Écran 2 stops table — mirrors [ImportedDeliveryRow] plus a stable [id] (the
 * backend contract has no per-row identifier for spreadsheet imports, unlike `POST /imports/pdf`'s
 * `tempId`) so a single row can be corrected in place (pencil icon, "Cliquez sur le crayon pour
 * corriger une ligne") without losing track of which row changed.
 */
@Serializable
data class WebImportRouteRow(
    val id: Int,
    val recipientName: String?,
    val address: String?,
    val phoneNumber: String?,
    val deadlineTime: String?,
    val hasMissingFields: Boolean,
    val packageCount: Int,
    /** HERE validation of [address], resolved server-side right after extraction (Écran 2 shows it per row). */
    val geocodeStatus: GeocodeStatus = GeocodeStatus.Pending,
    val latitude: Double? = null,
    val longitude: Double? = null,
    /** Retour 26/10 (R1/R11) — why HERE refused [address] (Failed only), null otherwise. */
    val geocodeFailureReason: GeocodeFailureReason? = null,
    /** Retour 29/10 — the address HERE matched ("Trouvée : …"), null when not reported (older data). */
    val resolvedAddress: String? = null,
) {
    /** Mirrors the mobile flow's own note: the opaque [hasMissingFields] flag isn't relied upon —
     * only [recipientName]/[address] nullity decides whether a row is incomplete (Écran 2b). */
    val isIncomplete: Boolean get() = recipientName.isNullOrBlank() || address.isNullOrBlank()

    /** R2 — motif of a row that blocks the next step, null when it doesn't. Same rule as
     * [com.milta.feature.route.domain.entity.DeliveryDraft]: no coordinates = never really validated. */
    val addressFixReason: GeocodeFailureReason?
        get() = addressFixReason(geocodeStatus, geocodeFailureReason)
            ?: GeocodeFailureReason.Unverified.takeIf { !address.isNullOrBlank() && (latitude == null || longitude == null) }

    /** R2 — to correct or remove before going on: refused by HERE, or no address at all. */
    val needsAddressFix: Boolean get() = address.isNullOrBlank() || addressFixReason != null
}

fun ImportedDeliveryRow.toRouteRow(id: Int) = WebImportRouteRow(
    id = id,
    recipientName = recipientName,
    address = address,
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
    hasMissingFields = hasMissingFields,
    packageCount = packageCount,
    geocodeStatus = geocodeStatus,
    latitude = latitude,
    longitude = longitude,
    geocodeFailureReason = geocodeFailureReason,
    resolvedAddress = resolvedAddress,
)

/**
 * Retour client 02/10 — importing several package labels/rows for the same destination created one
 * stop per row instead of one stop carrying several packages, on the web wizard specifically (Écran
 * 2 never called any consolidation at all, unlike the backend's `DeliveryConsolidator`/mobile's
 * `mergeDeliveryDraft`, which already do this for their own pipelines). Same match rule as both of
 * those — address + recipient name, trimmed/case-insensitive, kept in sync with all three — summing
 * [WebImportRouteRow.packageCount] and keeping the earliest non-null [WebImportRouteRow.deadlineTime].
 * A row with a blank/missing address or name is never merged into anything — merging two incomplete
 * rows just because both happen to be blank would silently hide genuinely distinct stops.
 */
fun consolidateWebImportRouteRows(rows: List<WebImportRouteRow>): List<WebImportRouteRow> {
    val merged = mutableListOf<WebImportRouteRow>()
    val indexByDestination = mutableMapOf<Pair<String, String>, Int>()

    for (row in rows) {
        val key = destinationKey(row)
        val existingIndex = key?.let { indexByDestination[it] }
        if (existingIndex != null) {
            val existing = merged[existingIndex]
            merged[existingIndex] = existing.copy(
                packageCount = existing.packageCount + row.packageCount,
                deadlineTime = listOfNotNull(existing.deadlineTime, row.deadlineTime).minOrNull(),
                hasMissingFields = existing.hasMissingFields || row.hasMissingFields,
            )
            continue
        }
        if (key != null) indexByDestination[key] = merged.size
        merged.add(row)
    }

    return merged
}

private fun destinationKey(row: WebImportRouteRow): Pair<String, String>? {
    val address = row.address?.trim()?.lowercase()
    val recipientName = row.recipientName?.trim()?.lowercase()
    if (address.isNullOrBlank() || recipientName.isNullOrBlank()) return null
    return address to recipientName
}

/** Mirrors [ImportedDeliveryRow.toRouteRow] for the PDF path (`POST /imports/pdf`,
 * [ExtractDeliveriesFromPdfUseCase]) — [ExtractedDelivery.tempId] is a backend-issued opaque
 * string, not reused as [WebImportRouteRow.id]: the same caller-assigned index used for the
 * spreadsheet path keeps row identity consistent regardless of import source. */
fun ExtractedDelivery.toRouteRow(id: Int) = WebImportRouteRow(
    id = id,
    recipientName = recipientName,
    address = address,
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
    hasMissingFields = missingFields.isNotEmpty(),
    packageCount = packageCount,
    geocodeStatus = geocodeStatus,
    latitude = latitude,
    longitude = longitude,
    geocodeFailureReason = geocodeFailureReason,
    resolvedAddress = resolvedAddress,
)

/** `recipientName`/`address` are required by `POST /routes` — a still-incomplete row is coerced to
 * blank rather than dropped, so the row count sent always matches what the table showed. */
fun WebImportRouteRow.toDraft() = DeliveryDraft(
    recipientName = recipientName.orEmpty(),
    address = address.orEmpty(),
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
    packageCount = packageCount,
    geocodeStatus = geocodeStatus,
    latitude = latitude,
    longitude = longitude,
    geocodeFailureReason = geocodeFailureReason,
    resolvedAddress = resolvedAddress,
)

/**
 * State for the web dashboard's "Ajouter une tournée" flow (MIL-50/MIL-51 — `WebImportRouteViewModel`).
 * [submitError] holds a stable error CODE resolved to a localized string in the screen, distinct
 * from the import [phase] errors above (Écran 2d/3's inline banners, not Écran 1's full-screen ones).
 */
data class WebImportRouteState(
    val phase: WebImportRoutePhase = WebImportRoutePhase.Idle,
    /**
     * Retour 26/10 — while [phase] is [WebImportRoutePhase.Analysing]: start of the analysis (the
     * chrono of the drop zone) and the real step reported by the server (null = not reported yet).
     * Kept here rather than in the `Analysing` object so that its existing comparisons stay valid.
     */
    val analysisStartedAtEpochMillis: Long? = null,
    val analysisProgress: ProcessingProgress? = null,
    /** "Annuler l'import" confirmed: back on Écran 1 with "Import annulé. Aucune livraison n'a été ajoutée." */
    val showCancelledNotice: Boolean = false,
    val fileName: String = "",
    val totalCount: Int = 0,
    val isStructuredFormat: Boolean = true,
    val rows: List<WebImportRouteRow> = emptyList(),
    val drivers: List<Driver> = emptyList(),
    val isLoadingDrivers: Boolean = false,
    /** Écran 2 (MIL-51) — replaces the former single `selectedDriverId`: any number of the
     * enterprise's drivers can now be checked, always submitted through `POST /route-batches`
     * (MIL-39), even when this set holds exactly one id. */
    val selectedDriverIds: Set<String> = emptySet(),
    val isSubmitting: Boolean = false,
    /** "driver_required" | "already_dispatched" | "route_not_found" | "validation_error" |
     * "optimization_unavailable" | "server_error" | "offline". Shared by the Écran 2 submit
     * (`POST /route-batches`) and the Écran 3 dispatch (`POST /route-batches/{id}/dispatch`) —
     * both are inline-banner failures on their own step, never a full-screen error. */
    val submitError: String? = null,
    /** Écran 3 (MIL-51) — result of a successful `POST /route-batches`, drives
     * [com.milta.web.WebRouteBatchResultScreen]. Cleared back to null only by starting a brand new
     * import ([WebImportRouteState] default), never by a failed dispatch — the manager stays on the
     * same split to retry. */
    val batchResult: RouteBatchResult? = null,
    /** Stops of each batch route, keyed by [com.milta.feature.route.domain.entity.RouteBatchDriverProposal.routeId]
     * — fetched one `GET /routes/{id}` (MIL-38, [com.milta.feature.route.domain.usecase.GetRouteDetailUseCase])
     * per driver right after [batchResult] is set, since `POST /route-batches`'s own response carries
     * no per-stop detail (name/address), only counts. */
    val batchStopsByRouteId: Map<String, List<Delivery>> = emptyMap(),
    /** Route ids whose `GET /routes/{id}` fetch in [batchStopsByRouteId]'s loading step failed —
     * lets the card tell "couldn't load the stops" apart from "this route genuinely has few/no
     * stops" instead of silently showing an empty preview for both. */
    val batchStopsFailedRouteIds: Set<String> = emptySet(),
    val isLoadingBatchStops: Boolean = false,
    val isDispatchingBatch: Boolean = false,
    /** MIL-99 — Écran 3, ids of the stops whose "Corriger l'adresse" PUT is in flight. */
    val fixingStopIds: Set<String> = emptySet(),
    /** RoutaR kill switch (ad hoc feature, 2026-08) — fail-open default while `GET /app-config`
     * hasn't resolved yet, see AppConfigRepository. When `false`, [WebVerifyAssignRouteScreen]
     * hides "Optimiser l'itinéraire" and only offers the existing
     * [WebImportRouteIntent.SubmitWithoutOptimization] CTA, never attempting the RoutaR call that
     * would fail every time. */
    val routarEnabled: Boolean = true,
    // ── MIL-99 — Adresse de chargement (Écran 2 card between "Assigner à" and the submit CTA) ──
    /** Sorted server-side, last used first — the first one is preselected on load. */
    val loadingAddresses: List<LoadingAddress> = emptyList(),
    /** `null` → nothing selected (empty list, or the selected one was just deleted): submit blocked. */
    val selectedLoadingAddressId: String? = null,
    val isLoadingLoadingAddresses: Boolean = false,
    val isSavingLoadingAddress: Boolean = false,
    /** Non-null while the delete confirmation dialog is shown for this address. */
    val loadingAddressPendingDeletion: LoadingAddress? = null,
    /** "server_error" | "offline" — list/add/delete failure, shown inside the card itself. */
    val loadingAddressError: String? = null,
    // ── MIL-97 — the counter itself is read from OptimizationQuotaStore by the screens ──
    /** Écran 3 (E2) — "Ré-optimiser" in flight (`POST /route-batches/{id}/reoptimize`). */
    val isReoptimizingBatch: Boolean = false,
    /** Reset date carried by the last quota answer (R11): 403 on "Ré-optimiser". */
    val quotaReachedResetAt: String? = null,
    // ── Retour 26/10 (demande 2) ──
    /** Table filter "Toutes / À corriger". */
    val filter: DeliveryFilter = DeliveryFilter.All,
    /** R7 — what [batchResult] was computed from: same fingerprint → shown again without RoutaR. */
    val batchFingerprint: RouteBatchFingerprint? = null,
    /** R8 — split of a draft restored after a reload (not in memory any more): abandoned before the next one. */
    val staleBatchId: String? = null,
    /** Modal "Modifier l'arrêt" / "Ajouter une livraison" (R4), null when closed. */
    val rowEditor: WebRowEditor? = null,
    /** R9 — a file added to the list is being analysed (its name), shown above the table. */
    val addedFileName: String? = null,
    /** R9 — that added file could not be imported: nothing was added. */
    val addedFileFailure: AddedFileFailure? = null,
) {
    /** R2 — rows to correct or remove before going on. */
    val toFixCount: Int get() = rows.count { it.needsAddressFix }

    val packagesCount: Int get() = rows.sumOf { it.packageCount.coerceAtLeast(1) }

    val visibleRows: List<WebImportRouteRow>
        get() = if (filter == DeliveryFilter.ToFix) rows.filter { it.needsAddressFix } else rows

    val selectedDrivers: List<Driver> get() = drivers.filter { it.id in selectedDriverIds }

    val selectedLoadingAddress: LoadingAddress?
        get() = loadingAddresses.firstOrNull { it.id == selectedLoadingAddressId }

    /** Derived from [rows] (mirrors [WebImportRouteRow.isIncomplete]) rather than stored — a stored
     * count computed once at import time would go stale the moment [WebImportRouteIntent.UpdateRow]
     * corrects or breaks a row, unlike the mobile equivalents (`SpreadsheetValidationState`,
     * `OcrValidationScreen`) which already recompute it the same way on every read. */
    val incompleteCount: Int get() = rows.count { it.isIncomplete }
}

/**
 * Retour client — serialized snapshot of an in-progress Écran 2 (persisted to
 * [com.milta.core.cache.LocalCacheStore] by [WebImportRouteViewModel]) so navigating away from
 * `/dashboard/import` and back (or a real page reload) doesn't silently discard an already-paid-for
 * OCR/AI scan, forcing a wasteful re-import. [isStructuredFormat]/[totalCount] aren't persisted —
 * only cosmetic (Écran 2b's "format non structuré" banner, the stops-detected count) and not worth
 * the extra fields; restored with safe defaults ([rows].size, `true`).
 */
@Serializable
data class ImportRouteDraft(
    val fileName: String,
    val rows: List<WebImportRouteRow>,
    val selectedDriverIds: Set<String>,
    /** Import (`/import-jobs`) the rows come from — null for a draft saved before async imports. */
    val jobId: String? = null,
    /** Retour 26/10 (R8) — split created and not sent yet when the draft was saved. */
    val batchId: String? = null,
)

/** Retour 26/10 (R9) — why a file added to the list could not be imported, shown with the texts
 * of the same failure on the first import (Écran 1). */
data class AddedFileFailure(val fileName: String, val reason: AddedFileFailureReason)

sealed interface AddedFileFailureReason {
    /** MIL-98 — AI credit at 0: a PDF refused, or a spreadsheet whose columns needed the AI. */
    data class AiCreditExhausted(val isSpreadsheet: Boolean, val resetAt: String? = null) : AddedFileFailureReason
    /** The list would go over [MAX_STOPS]. */
    data object TooManyStops : AddedFileFailureReason
    /** The document itself (unreadable, too long, too large, no deliveries, file expired). */
    data class Document(val kind: ImportJobFailureKind) : AddedFileFailureReason
    data object ServiceUnavailable : AddedFileFailureReason
    data object Unknown : AddedFileFailureReason
}

/** Retour 26/10 (R4) — the row being edited ([rowId]) or added (null). [refusal] = HERE refused the
 * address at "Enregistrer": nothing saved, the motif and an example are shown under the field. */
data class WebRowEditor(
    val rowId: Int?,
    val isSaving: Boolean = false,
    val refusal: GeocodeFailureReason? = null,
    val refusedAddress: String = "",
)
